// frontend/src/askos/AskosDashboard.js
//
// Dashboard shell + left nav for the Samkalp AskOS experience, matching
// the design mockup at /askos-dashboard.html (repo root). Nav destinations
// are nested React Router routes under /samkalp-ias/* (deep-linkable).
import React, { useEffect, useRef, useState } from 'react';
import { Routes, Route, NavLink } from 'react-router-dom';
import api, { askosAuthHeader } from './askosApi';
import DashboardHome from './DashboardHome';
import AskosChat from './AskosChat';
import MySubjectsPage from './MySubjectsPage';
import CurrentAffairsPage from './CurrentAffairsPage';
import LiveSessionsPage from './liveSessions/LiveSessionsPage';
import BookMentoringPage from './mentoring/BookMentoringPage';
import MyMentoringBookingsPage from './mentoring/MyMentoringBookingsPage';
import SettingsPage from './SettingsPage';
import PyqPracticePage from './pyq/PyqPracticePage';
import ContentReaderPage from './reader/ContentReaderPage';
import FacultyResourceReaderPage from './reader/FacultyResourceReaderPage';
import SavedResourcesPage from './SavedResourcesPage';
import StudyPlanPage from './StudyPlanPage';
import PerformancePage from './PerformancePage';
import MockTestsPage from './mocktest/MockTestsPage';
import MockTestRunner from './mocktest/MockTestRunner';
import MockTestResultPage from './mocktest/MockTestResultPage';
import './AskosDashboard.css';

const NAV_ITEMS = [
    { key: 'dashboard', label: 'Dashboard', path: '', icon: '⌂' },
    { key: 'ask-askos', label: 'Ask AskOS', path: 'ask-askos', icon: '◌' },
    { key: 'my-subjects', label: 'My Subjects', path: 'my-subjects', icon: '▤' },
    { key: 'live-sessions', label: 'Live Sessions', path: 'live-sessions', icon: '◉' },
    { key: 'mentoring', label: '1:1 Mentoring', path: 'mentoring', icon: '☺' },
    { key: 'current-affairs', label: 'Current Affairs', path: 'current-affairs', icon: '▧' },
    { key: 'pyq-practice', label: 'PYQ Practice', path: 'pyq-practice', icon: '▣' },
    { key: 'study-plan', label: 'Study Plan', path: 'study-plan', icon: '□' },
    { key: 'saved-resources', label: 'Saved Resources', path: 'saved-resources', icon: '⌑' },
    { key: 'performance', label: 'Performance', path: 'performance', icon: '▥' },
    { key: 'mock-tests', label: 'Mock Tests', path: 'mock-tests', icon: '◎' },
    { key: 'settings', label: 'Settings', path: 'settings', icon: '⚙' },
];

const initials = (name) => (name || 'A')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');

const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
};

const AskosDashboard = ({ slug, onLogout }) => {
    const [profile, setProfile] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const shellRef = useRef(null);

    useEffect(() => {
        // Per-academy theming: only overrides --navy/--gold when the tenant
        // has set custom colors; unset tenants keep this file's hardcoded
        // defaults untouched. --navy/--gold are declared directly on
        // .app-shell (AskosDashboard.css), not :root, so the override must
        // be set on that same element (an inline style always wins over a
        // stylesheet rule on the SAME element; setting on :root would not,
        // since .app-shell's own declaration would still take precedence
        // over an inherited value). Scoped to AskOS only — Studio/Classroom/
        // Samkalp Admin never read these variables.
        api.get(`/public/tenant-branding/${slug}`)
            .then(res => {
                const el = shellRef.current;
                if (!el) return;
                if (res.data.theme_primary) el.style.setProperty('--navy', res.data.theme_primary);
                if (res.data.theme_accent) el.style.setProperty('--gold', res.data.theme_accent);
            })
            .catch(() => {});
    }, [slug]);

    useEffect(() => {
        api.get(`/tenant/${slug}/askos/me`, { headers: askosAuthHeader() })
            .then(res => setProfile(res.data))
            .catch(() => {
                // Token invalid/expired for this tenant — fall back to login.
                onLogout && onLogout();
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    const firstName = profile?.full_name ? profile.full_name.split(' ')[0] : 'Aspirant';

    return (
        <div className="app-shell" ref={shellRef}>
            <aside className="sidebar">
                <div className="brand">
                    <div className="samkalp-logo">
                        <div className="sun">☀</div>
                        <div><strong>SAMKALP</strong><span>IAS ACADEMY</span></div>
                    </div>
                    <div className="brand-tagline">संकल्प से सफलता तक</div>
                </div>

                <div className="askos-mark">Ask<span>OS</span></div>

                <nav className="sidebar-nav">
                    {NAV_ITEMS.map(item => (
                        <NavLink
                            key={item.key}
                            to={item.path ? `/samkalp-ias/${item.path}` : '/samkalp-ias'}
                            end={item.path === ''}
                            className={({ isActive }) => (isActive ? 'active' : '')}
                        >
                            <span>{item.icon}</span>{item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="sidebar-spacer" />

                <div className="motivation-card">
                    <div className="motivation-copy">
                        <strong>Stay consistent.<br />Stay inspired.</strong>
                        <span>Success is a habit.</span>
                    </div>
                    <div className="monument">🏛️</div>
                </div>

                <div className="upgrade-card">
                    <div className="upgrade-title">♛ Upgrade to Pro</div>
                    <p>Unlock advanced insights, premium resources &amp; more.</p>
                    <button type="button">Upgrade Now</button>
                </div>
            </aside>

            <main className="main-content">
                <header className="topbar">
                    <div>
                        <h1>{greeting()}, {firstName}! 👋</h1>
                        <p>Ready to make today productive?</p>
                    </div>
                    <div className="topbar-actions">
                        <label className="search-box">
                            <span>⌕</span>
                            <input type="text" placeholder="Search for topics, resources, PYQs..." />
                        </label>
                        <button className="icon-btn" aria-label="Notifications" type="button">🔔</button>
                        <div className="profile profile-dropdown">
                            <div className="avatar">
                                {profile?.profile_photo
                                    ? <img src={profile.profile_photo} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                    : initials(profile?.full_name)}
                            </div>
                            <div>
                                <strong>{profile?.full_name || '...'}</strong>
                                <span>UPSC CSE Aspirant</span>
                            </div>
                            <button className="profile-menu" type="button" onClick={() => setMenuOpen(o => !o)}>⌄</button>
                            {menuOpen && (
                                <div className="profile-dropdown-menu">
                                    <button type="button" onClick={onLogout}>Log out</button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <Routes>
                    <Route index element={<DashboardHome slug={slug} firstName={firstName} />} />
                    <Route path="ask-askos" element={<AskosChat slug={slug} />} />
                    <Route path="my-subjects" element={<MySubjectsPage slug={slug} />} />
                    <Route path="live-sessions" element={<LiveSessionsPage slug={slug} />} />
                    <Route path="mentoring" element={<BookMentoringPage slug={slug} />} />
                    <Route path="mentoring/my-bookings" element={<MyMentoringBookingsPage slug={slug} />} />
                    <Route path="current-affairs" element={<CurrentAffairsPage slug={slug} />} />
                    <Route path="pyq-practice" element={<PyqPracticePage slug={slug} />} />
                    <Route path="reader/:projectId/:unitId" element={<ContentReaderPage slug={slug} />} />
                    <Route path="resources/:projectId/:materialId" element={<FacultyResourceReaderPage slug={slug} />} />
                    <Route path="study-plan" element={<StudyPlanPage slug={slug} />} />
                    <Route path="saved-resources" element={<SavedResourcesPage slug={slug} />} />
                    <Route path="performance" element={<PerformancePage slug={slug} />} />
                    <Route path="mock-tests" element={<MockTestsPage slug={slug} />} />
                    <Route path="mock-tests/:testId/attempt/:attemptId" element={<MockTestRunner slug={slug} />} />
                    <Route path="mock-tests/result/:attemptId" element={<MockTestResultPage slug={slug} />} />
                    <Route path="settings" element={<SettingsPage slug={slug} />} />
                </Routes>
            </main>
        </div>
    );
};

export default AskosDashboard;
